/**
 * useSwipeGesture — shared low-level horizontal swipe detector.
 *
 * Single source of truth for the touch mechanics used by every horizontal
 * swipe surface in the app (the top header SectionSwitcher ribbon and the
 * page-body content swipe, via useTabSwipe). Behavior:
 *  - axis-lock on first 6px of movement to distinguish horizontal vs vertical
 *  - 50px swipe threshold (configurable)
 *  - passive touchstart + touchend, non-passive touchmove (only calls
 *    preventDefault once a horizontal swipe is confirmed, so vertical scroll
 *    is left completely untouched)
 *  - discrete fire-on-release — no live finger-following/drag-follow
 *  - [data-no-swipe] attribute on child elements suppresses swipe (e.g.
 *    scrollable pill rows nested inside a swipeable container)
 *  - attached via callback ref so it works across mount/unmount cycles
 *    (e.g. AnimatePresence)
 *
 * Usage:
 *   const { swipeCallbackRef } = useSwipeGesture({
 *     onSwipeLeft: () => advance(),
 *     onSwipeRight: () => goBack(),
 *   });
 *   <div ref={swipeCallbackRef} style={{ overflow: 'hidden', touchAction: 'pan-y' }}>
 *     ...content...
 *   </div>
 */

import { useRef, useCallback, useEffect } from 'react';

export interface UseSwipeGestureOptions {
  /** Called once when a horizontal swipe left (finger moves left) crosses the threshold */
  onSwipeLeft: () => void;
  /** Called once when a horizontal swipe right (finger moves right) crosses the threshold */
  onSwipeRight: () => void;
  /** Minimum horizontal distance in pixels to fire a swipe callback (default: 50) */
  threshold?: number;
  /** When false the gesture is inert — no axis lock, no preventDefault, no callbacks */
  enabled?: boolean;
}

export function useSwipeGesture({
  onSwipeLeft,
  onSwipeRight,
  threshold = 50,
  enabled = true,
}: UseSwipeGestureOptions) {
  // Keep a ref for the DOM node so we can detach on unmount
  const nodeRef = useRef<HTMLElement | null>(null);

  const enabledRef = useRef<boolean>(enabled);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const thresholdRef = useRef<number>(threshold);
  useEffect(() => {
    thresholdRef.current = threshold;
  }, [threshold]);

  // Latest callback refs so the (stable) DOM listeners always invoke the
  // freshest closures without ever needing to be re-attached.
  const onSwipeLeftRef = useRef(onSwipeLeft);
  useEffect(() => {
    onSwipeLeftRef.current = onSwipeLeft;
  }, [onSwipeLeft]);

  const onSwipeRightRef = useRef(onSwipeRight);
  useEffect(() => {
    onSwipeRightRef.current = onSwipeRight;
  }, [onSwipeRight]);

  // Swipe state
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const axisLocked = useRef<'horizontal' | 'vertical' | null>(null);

  const dispatchTouchStart = useCallback((e: TouchEvent) => {
    if (!enabledRef.current) return;
    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
    axisLocked.current = null;
  }, []);

  const dispatchTouchMove = useCallback((e: TouchEvent) => {
    if (!enabledRef.current) return;
    // Let [data-no-swipe] elements (scrollable pills, carousels) handle their own scroll
    const target = e.target as Element;
    if (target.closest?.('[data-no-swipe]')) return;

    const touch = e.touches[0];
    const dx = touch.clientX - touchStartX.current;
    const dy = touch.clientY - touchStartY.current;

    // Lock to axis on first significant movement
    if (axisLocked.current === null) {
      if (Math.abs(dx) > 6 || Math.abs(dy) > 6) {
        axisLocked.current = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
      }
    }

    // Prevent native scroll only while tracking a horizontal swipe
    if (axisLocked.current === 'horizontal') {
      e.preventDefault();
    }
  }, []);

  const dispatchTouchEnd = useCallback((e: TouchEvent) => {
    if (!enabledRef.current) return;
    if (axisLocked.current !== 'horizontal') return;

    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStartX.current;

    if (Math.abs(dx) < thresholdRef.current) return;

    if (dx < 0) {
      // Swipe left → advance
      onSwipeLeftRef.current();
    } else {
      // Swipe right → go back
      onSwipeRightRef.current();
    }
  }, []);

  const swipeCallbackRef = useCallback((el: HTMLElement | null) => {
    // Detach from previous node
    const prev = nodeRef.current;
    if (prev) {
      prev.removeEventListener('touchstart', dispatchTouchStart);
      prev.removeEventListener('touchmove', dispatchTouchMove);
      prev.removeEventListener('touchend', dispatchTouchEnd);
    }

    nodeRef.current = el;

    if (!el) return;

    el.addEventListener('touchstart', dispatchTouchStart, { passive: true });
    el.addEventListener('touchmove', dispatchTouchMove, { passive: false });
    el.addEventListener('touchend', dispatchTouchEnd, { passive: true });
  }, [dispatchTouchStart, dispatchTouchMove, dispatchTouchEnd]);

  return { swipeCallbackRef };
}
